// FunctionalSpecification.js — Documentation Fonctionnelle : POC « Agents » (module Environnement).
// Réplique INTERACTIVE et fidèle de l'écran réel de gestion des Agents (liste + détail à onglets
// Informations/Actions + formulaire de création + test de connectivité) DOUBLÉE d'un guide utilisateur
// pas-à-pas (créer / modifier / tester). Les actions sont SIMULÉES localement (démo, aucun appel réseau).
// Source de vérité : automata-studio-frontend [AUT][02]-Agents.js + automata_environment AgentController.
import React, { useState } from 'react';
import {
  Plus, Wifi, WifiOff, Server, Edit, X, Save, Power, PowerOff, Activity, CheckCircle, XCircle,
  Cpu, Globe, Zap, FolderCog, User, Lock, CalendarDays, Clock, Info, ArrowRight, Search,
} from 'lucide-react';
import { Card, CardContent, Button, Badge, Tabs, Icon } from '../ui';

// 8 types d'agents proposés par l'écran réel (libellés exacts).
const AGENT_TYPES = [
  { value: 'GENERO_GDC', label: 'Genero GDC', icon: 'Settings', color: '#8B5CF6' },
  { value: 'DATABASE_IFX', label: 'Informix Database', icon: 'Database', color: '#06B6D4' },
  { value: 'DATABASE_ORA', label: 'Oracle Database', icon: 'Database', color: '#F97316' },
  { value: 'OS_LINUX', label: 'Linux OS', icon: 'Terminal', color: '#10B981' },
  { value: 'OS_WINDOWS', label: 'Windows OS', icon: 'SquareTerminal', color: '#2563EB' },
  { value: 'API_REST', label: 'REST API', icon: 'Globe', color: '#10B981' },
  { value: 'API_SOAP', label: 'SOAP API', icon: 'Globe', color: '#059669' },
  { value: 'WEB_BROWSER', label: 'Navigateur Web', icon: 'AppWindow', color: '#EC4899' },
];
const typeOf = (v) => AGENT_TYPES.find((t) => t.value === v) || { label: v, icon: 'Server', color: '#6B7280' };

const SAMPLE = [
  { id: 1, agentType: 'GENERO_GDC', ipAddress: '10.0.8.70', port: 6400, installationPath: '/app/sopra/amplitude/v11', login: 'automata', password: 'x', isActive: true, environment: { appName: 'BGFI-Recette' }, createdBy: 'admin', createdDate: '2026-06-10', modifiedBy: 'admin', modifiedDate: '2026-06-14' },
  { id: 2, agentType: 'DATABASE_ORA', ipAddress: '10.20.3.10', port: 1521, installationPath: '', login: 'app_user', password: 'x', isActive: true, environment: null, createdBy: 'admin', createdDate: '2026-06-11', modifiedBy: 'admin', modifiedDate: '2026-06-12' },
  { id: 3, agentType: 'OS_LINUX', ipAddress: '10.20.5.40', port: 22, installationPath: '', login: 'automata', password: 'x', isActive: false, environment: null, createdBy: 'ops', createdDate: '2026-06-09', modifiedBy: 'ops', modifiedDate: '2026-06-09' },
];
const EMPTY_FORM = { agentType: '', ipAddress: '', port: '', installationPath: '', login: '', password: '', isActive: true };

// ---- Petits sous-composants de la réplique ---------------------------------
const TypeIcon = ({ agentType, size = 'h-5 w-5', box = 'h-10 w-10' }) => {
  const t = typeOf(agentType);
  return <span className={`${box} rounded-xl flex items-center justify-center shrink-0`} style={{ backgroundColor: t.color }}><Icon name={t.icon} className={`${size} text-white`} /></span>;
};

const StatusBadge = ({ active }) => active
  ? <Badge color="#10B981"><Wifi className="h-3 w-3" />Actif</Badge>
  : <Badge><WifiOff className="h-3 w-3" />Inactif</Badge>;

const StatCard = ({ icon: IconCmp, label, value }) => (
  <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1"><IconCmp className="h-4 w-4" />{label}</div>
    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 break-all">{value || 'N/A'}</div>
  </div>
);

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// ---- Page -------------------------------------------------------------------
const AgentPOC = () => {
  const [agents, setAgents] = useState(SAMPLE);
  const [selectedId, setSelectedId] = useState(SAMPLE[0].id);
  const [mode, setMode] = useState('detail'); // 'detail' | 'create'
  const [tab, setTab] = useState('info'); // 'info' | 'actions'
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [test, setTest] = useState('idle'); // 'idle' | 'testing' | 'ok' | 'ko'

  const selected = agents.find((a) => a.id === selectedId) || null;
  const filtered = agents.filter((a) =>
    (statusFilter === 'all' || (statusFilter === 'active' ? a.isActive : !a.isActive)) &&
    (`${a.ipAddress} ${a.agentType} ${a.installationPath}`.toLowerCase().includes(search.toLowerCase()))
  );

  const openCreate = () => { setForm(EMPTY_FORM); setMode('create'); };
  const submitCreate = () => {
    if (!form.agentType || !form.ipAddress || !form.port) return;
    const id = Math.max(0, ...agents.map((a) => a.id)) + 1;
    const created = { ...form, id, port: parseInt(form.port, 10) || form.port, environment: null, createdBy: 'vous', createdDate: '2026-06-15', modifiedBy: 'vous', modifiedDate: '2026-06-15' };
    setAgents((prev) => [...prev, created]);
    setSelectedId(id); setMode('detail'); setTab('info'); setTest('idle');
  };
  const toggleActive = () => { setAgents((prev) => prev.map((a) => a.id === selectedId ? { ...a, isActive: !a.isActive } : a)); setTest('idle'); };
  const runTest = () => {
    setTest('testing');
    const ok = !!selected?.isActive;
    window.setTimeout(() => setTest(ok ? 'ok' : 'ko'), 700);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));

  return (
    <div className="space-y-8">
      {/* Intro */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Documentation Fonctionnelle — Agents</h1>
          <Badge color="#3B82F6">Module Environnement</Badge>
          <Badge color="#F59E0B">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">Aperçu interactif de l'écran <strong>Agents</strong> : liste, détail, création et test de connectivité. <span className="text-gray-500 dark:text-gray-400">Les actions ci-dessous sont simulées localement (démo) — aucun appel réseau.</span></p>
      </div>

      {/* ===== Réplique interactive de l'IHM ===== */}
      <Card className="overflow-hidden">
        {/* En-tête type app */}
        <div className="px-5 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Server className="h-6 w-6" />
            <div>
              <h2 className="text-lg font-bold leading-tight">Agents</h2>
              <p className="text-xs text-white/80">{agents.length} agent(s) configuré(s)</p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-white/20 hover:bg-white/30 text-white"><Plus className="h-4 w-4" />Nouvel agent</Button>
        </div>

        <div className="flex flex-col lg:flex-row">
          {/* Panneau gauche : liste */}
          <div className="lg:w-[360px] shrink-0 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 p-4">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un agent..." className="w-full pl-10 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none" />
            </div>
            <div className="flex gap-2 mb-3">
              {[['all', 'Tous'], ['active', 'Actifs'], ['inactive', 'Inactifs']].map(([k, lbl]) => (
                <button key={k} onClick={() => setStatusFilter(k)} className={`px-3 py-1 rounded-full text-xs font-medium ${statusFilter === k ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>{lbl}</button>
              ))}
            </div>
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {filtered.map((a) => {
                const on = selected?.id === a.id && mode === 'detail';
                return (
                  <button key={a.id} onClick={() => { setSelectedId(a.id); setMode('detail'); setTab('info'); setTest('idle'); }}
                    className={`w-full text-left p-3 rounded-xl border transition-all ${on ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}>
                    <div className="flex items-center gap-3">
                      <TypeIcon agentType={a.agentType} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2"><span className="font-semibold text-sm text-gray-900 dark:text-white truncate">{a.ipAddress}:{a.port}</span></div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{typeOf(a.agentType).label}</div>
                      </div>
                      <StatusBadge active={a.isActive} />
                    </div>
                    {a.environment && <div className="mt-2"><Badge color="#3B82F6"><Server className="h-3 w-3" />{a.environment.appName}</Badge></div>}
                  </button>
                );
              })}
              {filtered.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Aucun agent</p>}
            </div>
          </div>

          {/* Panneau droit : détail ou création */}
          <div className="flex-1 p-5 min-w-0">
            {mode === 'create' ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Nouvel agent</h3>
                  <button onClick={() => setMode('detail')} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Type d'agent <span className="text-red-500">*</span></span>
                    <select value={form.agentType} onChange={set('agentType')} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500">
                      <option value="">— Choisir —</option>
                      {AGENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </label>
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Adresse IP <span className="text-red-500">*</span></span>
                    <input value={form.ipAddress} onChange={set('ipAddress')} placeholder="10.0.0.1" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500" />
                  </label>
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Port <span className="text-red-500">*</span></span>
                    <input type="number" value={form.port} onChange={set('port')} placeholder="6400" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500" />
                  </label>
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Chemin d'installation</span>
                    <input value={form.installationPath} onChange={set('installationPath')} placeholder="/app/..." className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500" />
                  </label>
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Login</span>
                    <input value={form.login} onChange={set('login')} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500" />
                  </label>
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Mot de passe</span>
                    <input type="password" value={form.password} onChange={set('password')} placeholder="Laisser vide pour conserver" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500" />
                  </label>
                </div>
                <label className="flex items-center gap-2 mt-4 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} className="h-4 w-4" />
                  Activer l'agent <span className="text-gray-400">({form.isActive ? 'Actif' : 'Inactif'})</span>
                </label>
                <div className="flex justify-end gap-2 mt-5">
                  <Button variant="ghost" onClick={() => setMode('detail')}>Annuler</Button>
                  <Button onClick={submitCreate} disabled={!form.agentType || !form.ipAddress || !form.port}><Plus className="h-4 w-4" />Créer</Button>
                </div>
                {(!form.agentType || !form.ipAddress || !form.port) && <p className="text-xs text-gray-400 mt-2">Champs requis : Type, Adresse IP et Port.</p>}
              </div>
            ) : selected ? (
              <div>
                {/* En-tête détail */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <TypeIcon agentType={selected.agentType} box="h-12 w-12" size="h-6 w-6" />
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">{selected.ipAddress}:{selected.port}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">{typeOf(selected.agentType).label}<span className={`inline-block h-2 w-2 rounded-full ${selected.isActive ? 'bg-emerald-400' : 'bg-gray-400'}`} /></div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm"><Edit className="h-4 w-4" />Modifier</Button>
                </div>

                <Tabs tabs={[{ id: 'info', label: 'Informations' }, { id: 'actions', label: 'Actions' }]} active={tab} onChange={setTab} className="mb-4" />

                {tab === 'info' ? (
                  <div className="space-y-4">
                    {/* État de l'agent */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        {selected.isActive ? <Wifi className="h-5 w-5 text-emerald-500" /> : <WifiOff className="h-5 w-5 text-gray-400" />}
                        <div><div className="text-sm font-medium text-gray-900 dark:text-white">État de l'agent</div><div className="text-xs text-gray-500 dark:text-gray-400">{selected.isActive ? 'Actif et opérationnel' : 'Inactif'}</div></div>
                      </div>
                      {selected.isActive
                        ? <Button variant="ghost" size="sm" onClick={toggleActive} className="text-red-600"><PowerOff className="h-4 w-4" />Désactiver</Button>
                        : <Button variant="ghost" size="sm" onClick={toggleActive} className="text-emerald-600"><Power className="h-4 w-4" />Activer</Button>}
                    </div>
                    {/* StatCards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <StatCard icon={Cpu} label="Type d'agent" value={typeOf(selected.agentType).label} />
                      <StatCard icon={Globe} label="Adresse IP" value={selected.ipAddress} />
                      <StatCard icon={Zap} label="Port" value={selected.port} />
                      <StatCard icon={FolderCog} label="Chemin d'installation" value={selected.installationPath} />
                      <StatCard icon={User} label="Login" value={selected.login} />
                      <StatCard icon={Lock} label="Mot de passe" value={selected.password ? '••••••••' : 'N/A'} />
                    </div>
                    {/* Infos additionnelles */}
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/60">
                      {[[CalendarDays, 'Créé le', selected.createdDate], [Clock, 'Dernière modification', selected.modifiedDate], [User, 'Créé par', selected.createdBy], [Server, 'Environnement associé', selected.environment?.appName || 'Aucun']].map(([Ic, l, v], i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                          <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400"><Ic className="h-4 w-4" />{l}</span>
                          <span className="text-gray-800 dark:text-gray-200">{v || 'N/A'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">Test de connectivité</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">Vérifier si l'agent est accessible</div>
                      {test === 'ok' && <div className="mb-3 flex items-center gap-2 p-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-sm"><CheckCircle className="h-4 w-4" />Connexion établie</div>}
                      {test === 'ko' && <div className="mb-3 flex items-center gap-2 p-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300 text-sm"><XCircle className="h-4 w-4" />Connexion échouée</div>}
                      <Button onClick={runTest} disabled={test === 'testing'} className="w-full justify-center bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
                        {test === 'testing' ? <>Test en cours…</> : <><Activity className="h-4 w-4" />Tester la connectivité</>}
                      </Button>
                      <p className="text-xs text-gray-400 mt-2">Démo : un agent <strong>actif</strong> renvoie « Connexion établie », un agent <strong>inactif</strong> « Connexion échouée ».</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-400">Sélectionnez un agent</div>
            )}
          </div>
        </div>
      </Card>

      {/* ===== Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Plus className="h-5 w-5 text-blue-500" />Créer un agent</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Cliquer sur <strong>« Nouvel agent »</strong> (en haut à droite). <button onClick={openCreate} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Choisir le <strong>Type d'agent</strong> (Genero, Oracle/Informix, Linux/Windows, REST/SOAP, Navigateur Web).</GuideStep>
              <GuideStep n={3}>Saisir <strong>Adresse IP</strong> et <strong>Port</strong> (1–65535) — champs obligatoires.</GuideStep>
              <GuideStep n={4}>Optionnel : Chemin d'installation, Login, Mot de passe.</GuideStep>
              <GuideStep n={5}>Laisser <strong>« Activer l'agent »</strong> coché puis <strong>Créer</strong>.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Edit className="h-5 w-5 text-blue-500" />Modifier un agent</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Sélectionner un agent dans la liste de gauche.</GuideStep>
              <GuideStep n={2}>Cliquer sur <strong>« Modifier »</strong> (en haut du détail).</GuideStep>
              <GuideStep n={3}>Ajuster les champs souhaités.</GuideStep>
              <GuideStep n={4}><strong>Mot de passe vide = conservé</strong> (ne pas ressaisir pour garder l'ancien).</GuideStep>
              <GuideStep n={5}>Onglet Informations → <strong>Activer / Désactiver</strong> bascule l'état. <button onClick={() => { setMode('detail'); setTab('info'); }} className="text-blue-600 dark:text-blue-400 underline">Voir</button></GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Activity className="h-5 w-5 text-blue-500" />Tester un agent</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Sélectionner l'agent, ouvrir l'onglet <strong>Actions</strong>. <button onClick={() => { setMode('detail'); setTab('actions'); }} className="text-blue-600 dark:text-blue-400 underline">Ouvrir</button></GuideStep>
              <GuideStep n={2}>Cliquer sur <strong>« Tester la connectivité »</strong>.</GuideStep>
              <GuideStep n={3}>Lire le résultat : <span className="text-emerald-600">Connexion établie</span> ou <span className="text-red-600">Connexion échouée</span>.</GuideStep>
              <GuideStep n={4}>Un agent doit être <strong>actif</strong> et <strong>déployé sur un environnement</strong> pour exécuter des tâches.</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== Référence des champs ===== */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Info className="h-5 w-5 text-blue-500" />Référence des champs du formulaire</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              <tr>{['Champ', 'Type', 'Requis', 'Détails'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
              {[
                ['Type d\'agent', 'Liste', 'Oui', '8 valeurs (Genero GDC, Informix, Oracle, Linux, Windows, REST, SOAP, Navigateur Web)'],
                ['Adresse IP', 'Texte', 'Oui', 'IP ou hostname de l\'agent'],
                ['Port', 'Nombre', 'Oui', 'Plage 1–65535'],
                ['Chemin d\'installation', 'Texte', 'Non', 'Chemin sur le serveur cible'],
                ['Login', 'Texte', 'Non', 'Identifiant de connexion'],
                ['Mot de passe', 'Mot de passe', 'Non', 'Vide à la modification = conservé'],
                ['Activer l\'agent', 'Interrupteur', 'Non', 'Actif par défaut'],
              ].map((r, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2 font-medium">{r[0]}</td><td className="px-4 py-2">{r[1]}</td>
                  <td className="px-4 py-2">{r[2] === 'Oui' ? <Badge color="#EF4444">Oui</Badge> : <Badge>Non</Badge>}</td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{r[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {/* ===== Règles de gestion + API ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Règles de gestion</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Type, Adresse IP et Port sont obligatoires ; le port doit être compris entre 1 et 65535.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />À la modification, un mot de passe vide conserve l'ancien.</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Un agent <strong>rattaché à un environnement</strong> ne peut pas être supprimé (le dissocier d'abord).</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Le <strong>déploiement</strong> sur un environnement exige un agent <strong>actif</strong>.</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Endpoints API</h3>
          <ul className="space-y-1.5 text-sm font-mono text-gray-700 dark:text-gray-200">
            {[['GET', '#10B981', '/api/automata/agents'], ['POST', '#3B82F6', '/api/automata/agents'], ['PUT', '#F59E0B', '/api/automata/agents/{id}'], ['DELETE', '#EF4444', '/api/automata/agents/{id}'], ['GET', '#10B981', '/api/automata/agents/{id}/test-connection'], ['PATCH', '#8B5CF6', '/api/automata/agents/{id}/active?active='], ['POST', '#3B82F6', '/api/automata/agents/{id}/deploy?environmentId=']].map((r, i) => (
              <li key={i} className="flex items-center gap-2"><Badge color={r[1]}>{r[0]}</Badge><span className="break-all">{r[2]}</span></li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : automata_environment / AgentController</p>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default AgentPOC;
